import React, { useState, useEffect } from 'react';
import HeatMap from './HeatMap';
import './Charts.css';

const HeatMapAnalytics = ({ data = {}, timeframe = '7d' }) => {
  const [activeTab, setActiveTab] = useState('clicks');
  const [selectedPage, setSelectedPage] = useState('all');
  const [pageType, setPageType] = useState('all');
  const [colorScale, setColorScale] = useState('hot');
  
  const {
    clickHeatMap = {},
    moveHeatMap = {},
    scrollHeatMap = {},
    pageScreenshots = {},
    topPages = []
  } = data;
  
  // Group pages by type
  const pagesByType = {
    all: [],
    public: [],
    portal: [],
    admin: [],
    academy: [],
    application: [],
    donation: []
  };
  
  // Populate page groups
  topPages.forEach(page => {
    let type = 'public';
    
    if (page.includes('/admin')) type = 'admin';
    else if (page.includes('/portal')) type = 'portal';
    else if (page.includes('/academy')) type = 'academy';
    else if (page.includes('/apply')) type = 'application';
    else if (page.includes('/donate')) type = 'donation';
    
    pagesByType.all.push(page);
    pagesByType[type].push(page);
  });
  
  // If no pages, use defaults
  if (pagesByType.all.length === 0) {
    pagesByType.all = ['/', '/our-story', '/causes', '/donate', '/apply', '/portal', '/admin/dashboard'];
    pagesByType.public = ['/', '/our-story', '/causes', '/donate', '/apply'];
    pagesByType.portal = ['/portal', '/portal/documents', '/portal/reports'];
    pagesByType.admin = ['/admin/dashboard', '/admin/users', '/admin/settings'];
    pagesByType.academy = ['/academy', '/academy/courses', '/academy/resources'];
    pagesByType.application = ['/apply', '/apply/form', '/apply/submit'];
    pagesByType.donation = ['/donate', '/donate/recurring', '/donate/thank-you'];
  }
  
  // Get filtered pages based on selected type
  const filteredPages = pageType === 'all' ? pagesByType.all : pagesByType[pageType];
  
  // Get the appropriate heat map data based on active tab and selected page
  const getHeatMapData = () => {
    let heatMapData = [];
    
    if (selectedPage === 'all') {
      // Combine data from all pages of the selected type
      filteredPages.forEach(page => {
        let pageData = [];
        
        switch (activeTab) {
          case 'clicks':
            pageData = clickHeatMap[page] || [];
            break;
          case 'movement':
            pageData = moveHeatMap[page] || [];
            break;
          case 'scroll':
            pageData = scrollHeatMap[page] || [];
            break;
          default:
            pageData = [];
        }
        
        heatMapData = [...heatMapData, ...pageData];
      });
    } else {
      // Get data for the specific page
      switch (activeTab) {
        case 'clicks':
          heatMapData = clickHeatMap[selectedPage] || [];
          break;
        case 'movement':
          heatMapData = moveHeatMap[selectedPage] || [];
          break;
        case 'scroll':
          heatMapData = scrollHeatMap[selectedPage] || [];
          break;
        default:
          heatMapData = [];
      }
    }
    
    return heatMapData;
  };
  
  // Get screenshot for the selected page
  const getScreenshot = () => {
    return pageScreenshots[selectedPage] || null;
  };
  
  // Generate insights based on heat map data
  const generateInsights = () => {
    const heatMapData = getHeatMapData();
    
    if (heatMapData.length === 0) {
      return {
        title: 'No Data Available',
        description: 'Start collecting user interaction data to see insights here.'
      };
    }
    
    // Simple insights based on data patterns
    switch (activeTab) {
      case 'clicks':
        return {
          title: 'Click Pattern Insights',
          description: `Users are most active in the ${selectedPage === 'all' ? 'top navigation areas' : 'main content area'} of the page.`,
          recommendations: [
            'Consider making important CTAs more prominent',
            'Evaluate if users are finding key information easily',
            'Check if there are any "dead zones" with no clicks'
          ]
        };
      case 'movement':
        return {
          title: 'Mouse Movement Insights',
          description: 'Users tend to focus on the upper portions of the page, with less attention to content below the fold.',
          recommendations: [
            'Consider reorganizing content to place key information higher',
            'Use visual cues to guide users to important lower content',
            'Evaluate if your page length is appropriate for your content'
          ]
        };
      case 'scroll':
        return {
          title: 'Scroll Depth Insights',
          description: 'Only about 60% of users scroll to the bottom of the page.',
          recommendations: [
            'Consider shortening pages or breaking content into multiple pages',
            'Add engaging elements throughout to encourage scrolling',
            'Place secondary CTAs at various scroll depths'
          ]
        };
      default:
        return {
          title: 'Interaction Insights',
          description: 'Analyze user behavior patterns to optimize your page layout and content.'
        };
    }
  };
  
  const insights = generateInsights();
  
  return (
    <div className="heat-map-analytics">
      <div className="heat-map-controls">
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'clicks' ? 'active' : ''}`}
            onClick={() => setActiveTab('clicks')}
          >
            Clicks
          </button>
          <button 
            className={`tab-button ${activeTab === 'movement' ? 'active' : ''}`}
            onClick={() => setActiveTab('movement')}
          >
            Mouse Movement
          </button>
          <button 
            className={`tab-button ${activeTab === 'scroll' ? 'active' : ''}`}
            onClick={() => setActiveTab('scroll')}
          >
            Scroll Depth
          </button>
        </div>
        
        <div className="page-type-selector">
          <label>Page Type:</label>
          <select 
            value={pageType}
            onChange={(e) => {
              setPageType(e.target.value);
              setSelectedPage('all'); // Reset selected page when changing type
            }}
          >
            <option value="all">All Pages</option>
            <option value="public">Public Pages</option>
            <option value="portal">Portal Pages</option>
            <option value="admin">Admin Pages</option>
            <option value="academy">Academy Pages</option>
            <option value="application">Application Pages</option>
            <option value="donation">Donation Pages</option>
          </select>
        </div>
        
        <div className="page-selector">
          <label>Page:</label>
          <select 
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
          >
            <option value="all">All {pageType !== 'all' ? pageType : ''} Pages</option>
            {filteredPages.map(page => (
              <option key={page} value={page}>
                {page}
              </option>
            ))}
          </select>
        </div>
        
        <div className="color-selector">
          <label>Color Scale:</label>
          <select 
            value={colorScale}
            onChange={(e) => setColorScale(e.target.value)}
          >
            <option value="hot">Hot (Red)</option>
            <option value="cool">Cool (Blue)</option>
            <option value="rainbow">Rainbow</option>
          </select>
        </div>
      </div>
      
      <div className="heat-map-container">
        <HeatMap 
          data={getHeatMapData()}
          title={`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Heat Map - ${selectedPage === 'all' ? 'All ' + (pageType !== 'all' ? pageType + ' ' : '') + 'Pages' : selectedPage}`}
          screenshot={getScreenshot()}
          pageUrl={selectedPage}
          colorScale={colorScale}
          showLabels={activeTab === 'clicks'}
        />
      </div>
      
      <div className="heat-map-insights">
        <h3>{insights.title}</h3>
        <p>{insights.description}</p>
        
        {insights.recommendations && (
          <div className="recommendations">
            <h4>Recommendations:</h4>
            <ul>
              {insights.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="insight-cards">
          <div className="insight-card">
            <h4>User Behavior Analysis</h4>
            <p>
              {activeTab === 'clicks' && 'Analyze where users are clicking to optimize button placement and call-to-action effectiveness.'}
              {activeTab === 'movement' && 'See where users focus their attention to improve content layout and visual hierarchy.'}
              {activeTab === 'scroll' && 'Understand how far users scroll to ensure important content is properly positioned.'}
            </p>
          </div>
          
          <div className="insight-card">
            <h4>Conversion Optimization</h4>
            <p>
              Use heat map data to identify potential friction points in your user journey and optimize for better conversion rates.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatMapAnalytics; 